import { Alert, Box, Button, Divider, Typography } from "@mui/material";
import AppDialog from "components/common/AppDialog";
import { CALIBRATE_FONT_FAMILY_MONO } from "components/hoc/Theme";
import type { SettingsSection } from "features/integrationsBeta/readinessUi";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useState } from "react";
import { usePendingChanges, type PendingChange } from "./PendingChangesContext";
import { diffConfig, formatPendingValue } from "./pendingFormat";
import {
  useFlushPendingChanges,
  type FlushFailure,
} from "./useFlushPendingChanges";

type Json = Record<string, unknown>;

const SECTION_ORDER: SettingsSection[] = [
  "status",
  "connections",
  "steps",
  "integration_config",
  "execution",
  "webhooks",
  "schedules",
];

function ConfigDiffRow({ change }: { change: PendingChange }) {
  const diffs = diffConfig(change.oldValue as Json, change.newValue as Json);
  return (
    <Typography variant="body2" component="li">
      {change.entityLabel !== "Integration" && (
        <Box component="span" fontWeight={700}>
          {change.entityLabel} -{" "}
        </Box>
      )}
      {change.fieldLabel}:
      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 2.5,
          fontFamily: CALIBRATE_FONT_FAMILY_MONO,
          fontSize: "0.8rem",
          "& li": { mb: 0.25 },
        }}
      >
        {diffs.map((d) => (
          <li key={d.key}>
            {d.kind === "changed" && (
              <>
                {d.key}: {d.oldText} {"->"} {d.newText}
              </>
            )}
            {d.kind === "added" && (
              <Box component="span" sx={{ color: "success.main" }}>
                {d.key}: {d.newText} (added)
              </Box>
            )}
            {d.kind === "removed" && (
              <Box component="span" sx={{ color: "error.main" }}>
                {d.key} (removed)
              </Box>
            )}
          </li>
        ))}
      </Box>
    </Typography>
  );
}

/**
 * Review of every staged change grouped by Settings section (UM
 * "Confirm Access Changes" style) with old -> new values; Confirm applies
 * the whole batch. Failed entities stay staged and are listed in an alert.
 */
export default function PendingReviewDialog({
  open,
  onClose,
  integrationId,
}: {
  open: boolean;
  onClose: () => void;
  integrationId: number;
}) {
  const dispatch = useAppDispatch();
  const { changes } = usePendingChanges();
  const flush = useFlushPendingChanges(integrationId);
  const [flushing, setFlushing] = useState(false);
  const [failures, setFailures] = useState<FlushFailure[]>([]);

  // Everything saved (or discarded elsewhere) -> nothing left to review.
  useEffect(() => {
    if (open && changes.length === 0 && !flushing) onClose();
  }, [open, changes.length, flushing]);

  const grouped = SECTION_ORDER.map((section) => ({
    section,
    rows: changes.filter((c) => c.section === section),
  })).filter((g) => g.rows.length > 0);

  const handleConfirm = async () => {
    setFlushing(true);
    setFailures([]);
    const total = changes.length;
    const fails = await flush(changes);
    setFlushing(false);
    if (fails.length === 0) {
      dispatch(
        pushNotification({
          severity: "success",
          message: `${total} change${total === 1 ? "" : "s"} saved.`,
        }),
      );
      onClose();
    } else {
      setFailures(fails);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={flushing ? () => {} : onClose}
      title="Review & Save Changes"
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <Button
            onClick={onClose}
            disabled={flushing}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={flushing}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {flushing ? "Saving..." : "Confirm & Save"}
          </Button>
        </>
      }
    >
      {failures.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Some changes could not be saved - they remain pending below.
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {failures.map((f, i) => (
              <li key={i}>
                <strong>{f.entityLabel}</strong>: {f.message}
              </li>
            ))}
          </Box>
        </Alert>
      )}
      {grouped.map(({ section, rows }, idx) => (
        <Box key={section}>
          {idx > 0 && <Divider sx={{ my: 2 }} />}
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={0.6}
            display="block"
            sx={{ mb: 1 }}
          >
            {rows[0].sectionLabel}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
            {rows.map((r) =>
              r.valueKind === "config" ? (
                <ConfigDiffRow key={r.key} change={r} />
              ) : (
                <Typography key={r.key} variant="body2" component="li">
                  {r.entityLabel !== "Integration" && (
                    <Box component="span" fontWeight={700}>
                      {r.entityLabel} -{" "}
                    </Box>
                  )}
                  {r.fieldLabel}: {formatPendingValue(r.valueKind, r.oldValue)}{" "}
                  {"->"}{" "}
                  <Box component="span" fontWeight={600}>
                    {formatPendingValue(r.valueKind, r.newValue)}
                  </Box>
                </Typography>
              ),
            )}
          </Box>
        </Box>
      ))}
    </AppDialog>
  );
}
